import { Reducer } from 'react';
import { TransactionListState } from 'store/transactions/transactionsTypes';
import { TransactionsActionTypes } from 'store/transactions/transactionsActionTypes';
import * as actionTypes from 'constants/action-types';
import { Pageable, SortConfiguration } from 'types/rest/paginationTypes';
import { clientSideFilterTransactions } from 'utils/transactionUtils';
import { Transaction } from 'types/rest/assetCatalogTypes';

export const initPageable: Pageable = {
  skip: 1,
  limit: 10,
};

export const transactionListInitialState: TransactionListState = {
  transactions: [],
  totalTransactions: 0,
  clientSideFilteredTransactions: [], //This is our client-side full list of filtered transactions
  filteredTransactions: [], //This is our client-side paged filtered transactions
  totalFilteredTransactions: 0,
  selectedTransactions: [],
  selectedTransactionsCount: 0,
  selectedEditorTab: 'request',
  selectAllType: null,
  activeVirtualService: 0,
  activeServiceMock: 0,
  currentSearchTerm: '',
  isCreatingNewTransaction: false,
  creatingNewTransactionType: 'HTTP',
  expandedTransactionId: 0,
  isLoading: true,
  txnsLoaded: false,
  isBulkTaggerOpen: false,
  sort: {} as SortConfiguration,
  pageable: initPageable,
  loadingText: '',
  resBodyInvalidMagicStrings: '',
  resHeaderInvalidMagicStrings: '',
  isResponseBodyMagicStringInvalid: false,
  isResponseHeaderMagicStringInvalid: false,
};

export const transactionReducer: Reducer<TransactionListState, TransactionsActionTypes> = (
  state: TransactionListState = transactionListInitialState,
  action: TransactionsActionTypes,
): TransactionListState => {
  switch (action.type) {
    case actionTypes.RESET_TRANSACTIONS_SELECTION:
      return {
        ...state,
        selectAllType: null,
        selectedTransactions: [],
        selectedTransactionsCount: 0,
      };
    case actionTypes.SELECT_TRANSACTIONS: {
      const isSelectAll = action.payload.selectAllType && action.payload.selectAllType === 'ALL';
      return {
        ...state,
        selectedTransactions: isSelectAll ? [] : action.payload.selectedIds,
        selectedTransactionsCount: isSelectAll ? state.totalTransactions : action.payload.selectedIds.length,
        selectAllType: action.payload.selectAllType ? action.payload.selectAllType : null,
      };
    }
    case actionTypes.GET_TRANSACTION_LIST:
      const filteredTransactions: Transaction[] = action.payload.transactions;
      const clientSideFilteredTransactions: Transaction[] = [];
      const totalFilteredTransactions: number = action.payload.total;
      const newPageable = { ...state.pageable };

      return {
        ...state,
        transactions: action.payload.transactions,
        totalTransactions: action.payload.total,
        clientSideFilteredTransactions: clientSideFilteredTransactions,
        filteredTransactions: filteredTransactions,
        totalFilteredTransactions: totalFilteredTransactions,
        // isSelectAllSelected: false,
        // selectedTransactions: [],
        // selectedTransactionsCount: 0,
        isLoading: action.payload.isLoading,
        txnsLoaded: true,
        pageable: newPageable,
      };

    case actionTypes.CLONE_TRANSACTION:
      const newTransactions: Transaction[] = state.transactions;
      newTransactions.concat(action.payload);
      return { ...state, transactions: newTransactions };
    case actionTypes.UPDATE_ACTIVE_PAGE: {
      const newPageable: Pageable = { ...state.pageable, skip: action.payload };

      return {
        ...state,
        pageable: newPageable,
      };
    }
    case actionTypes.SET_TRANSACTION_PAGE_SIZE: {
      const newPageable: Pageable = { ...state.pageable, limit: action.payload };

      return {
        ...state,
        pageable: newPageable,
      };
    }

    case actionTypes.BULK_TAG_TRANSACTION:
      return state;

    case actionTypes.CHANGE_EDITOR_TAB: {
      return { ...state, selectedEditorTab: action.payload };
    }

    case actionTypes.OPEN_TRANSACTION_BULK_TAG_MODAL:
      return {
        ...state,
        isBulkTaggerOpen: true,
      };
    case actionTypes.TRANSACTION_FILTER_CHANGED:
      return {
        ...state,
        currentSearchTerm: action.payload.searchTerm,
        activeVirtualService: action.payload.serviceId,
        activeServiceMock: action.payload.serviceMockId,
        //Reset selection
        selectAllType: null,
        selectedTransactionsCount: 0,
        selectedTransactions: [],
        isLoading: true,
        pageable: { ...state.pageable, skip: 1 },
      };

    case actionTypes.CLOSE_TRANSACTION_BULK_TAG_MODAL:
      return {
        ...state,
        isBulkTaggerOpen: false,
      };

    case actionTypes.LOADING_TRANSACTION_LIST:
      return {
        ...state,
        isLoading: true,
        loadingText: action.payload.loadingText,
      };

    case actionTypes.OPEN_HTTP_TRANSACTION_EDITOR_FOR_CREATE:
      return {
        ...state,
        creatingNewTransactionType: action.payload.transactionType,
        isCreatingNewTransaction: true,
      };

    case actionTypes.OPEN_MSG_TRANSACTION_EDITOR_FOR_CREATE:
      return {
        ...state,
        creatingNewTransactionType: 'MESSAGING',
        isCreatingNewTransaction: true,
      };

    case actionTypes.CLOSE_TRANSACTION_EDITOR_FOR_CREATE:
      return {
        ...state,
        expandedTransactionId: null,
        isCreatingNewTransaction: false,
      };

    case actionTypes.SORT_TXN_TABLE:
      return {
        ...state,
        sort: action.payload,
      };

    case actionTypes.UPDATE_TRANSACTION:
      return {
        ...state,
        expandedTransactionId: action.payload.expandedTransactionId,
        isResponseBodyMagicStringInvalid: action.payload.isResponseBodyMagicStringInvalid,
        isResponseHeaderMagicStringInvalid: action.payload.isResponseHeaderMagicStringInvalid,
        resBodyInvalidMagicStrings:
          action.payload.resBodyInvalidMagicStrings.length > 1 ? action.payload.resBodyInvalidMagicStrings : '',
        resHeaderInvalidMagicStrings:
          action.payload.resHeaderInvalidMagicStrings.length > 1 ? action.payload.resHeaderInvalidMagicStrings : '',
      };

    case actionTypes.EXPAND_TRANSACTION_ROW:
      return {
        ...state,
        expandedTransactionId: action.payload,
        isResponseBodyMagicStringInvalid: false,
        isResponseHeaderMagicStringInvalid: false,
        selectedEditorTab: 'request', //Selects request tab by default
      };

    case actionTypes.FILTER_CLIENT_SIDE_TRANSACTIONS: {
      let filteredTransactions: Transaction[] = [];
      let clientSideFilteredTransactions: Transaction[] = [];
      let totalFilteredTransactions: number = filteredTransactions.length;

      console.debug("Doing client side filtering using search term '%s'", action.payload);
      clientSideFilteredTransactions = clientSideFilterTransactions([...state.transactions], action.payload);
      totalFilteredTransactions = clientSideFilteredTransactions.length;
      filteredTransactions = clientSidePage(clientSideFilteredTransactions, initPageable);

      return {
        ...state,
        clientSideFilteredTransactions: clientSideFilteredTransactions,
        filteredTransactions: filteredTransactions,
        totalFilteredTransactions: totalFilteredTransactions,
        currentSearchTerm: action.payload,
        isLoading: false,
        pageable: initPageable,
      };
    }
    case actionTypes.SWITCH_WORKSPACE:
      return transactionListInitialState; // when switching workspaces reset everything to initial state

    default:
      return state;
  }
};

const clientSidePage = (transactionsToPage: Transaction[], pageable: Pageable) => {
  let start = (pageable.skip - 1) * pageable.limit;
  let end = start + pageable.limit;

  if (start < 0) {
    start = 0;
  }

  if (end > transactionsToPage.length) {
    end = transactionsToPage.length;
  }

  return transactionsToPage.slice(start, end);
};
